import { StyleSheet, css } from 'aphrodite'
import { useState } from 'react'
import { userService } from '../../services/userService'
import type { StakeholderType } from '../../types/user'

interface SaveToDirectoryDialogProps {
  /** The email address of the contact to save */
  email: string
  /** The name of the contact (optional) */
  name?: string
  /** The phone number of the contact (optional) */
  phoneNumber?: string
  /** Stakeholder types for the contact (optional) */
  stakeholderTypes?: StakeholderType[]
  /** Called when user confirms and contact is saved to directory */
  onConfirm: () => void
  /** Called when user clicks No - use contact without saving (optional) */
  onSkip?: () => void
  /** Called when dialog should close without action (optional - defaults to onSkip or onConfirm) */
  onCancel?: () => void
  /** Whether to show contact preview in the dialog */
  showPreview?: boolean
  /** Whether the parent will handle saving (if true, onConfirm is called immediately without internal save) */
  externalSave?: boolean
  /** Loading state controlled by parent (for externalSave mode) */
  isLoading?: boolean
}

/**
 * Dialog that asks user if they want to save new contact information
 * to their Directory/Address book.
 *
 * Supports two modes:
 * 1. Internal save (default): Dialog handles the API call to save contact
 * 2. External save: Parent handles saving, dialog just provides UI
 *
 * Used by:
 * - ContactAutocomplete (project terms manual entry)
 * - DesignatedDriverForm (work order drivers)
 */
const SaveToDirectoryDialog: React.FC<SaveToDirectoryDialogProps> = ({
  email,
  name,
  phoneNumber,
  stakeholderTypes = [],
  onConfirm,
  onSkip,
  onCancel,
  showPreview = true,
  externalSave = false,
  isLoading: externalLoading,
}) => {
  const [internalLoading, setInternalLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isLoading = externalSave ? externalLoading : internalLoading

  const handleConfirm = async () => {
    if (externalSave) {
      // Parent handles save
      onConfirm()
      return
    }

    // Internal save mode
    setInternalLoading(true)
    setError(null)

    try {
      await userService.addContact({
        email,
        name,
        phone_number: phoneNumber,
        stakeholder_types: stakeholderTypes,
      })
      onConfirm()
    } catch (err: unknown) {
      console.error('Failed to save contact to directory:', err)
      // Check if it's a duplicate contact error
      if (
        err &&
        typeof err === 'object' &&
        'response' in err &&
        err.response &&
        typeof err.response === 'object' &&
        'status' in err.response &&
        err.response.status === 400
      ) {
        setError('Contact already exists in your directory')
      } else {
        setError('Failed to save contact. Please try again.')
      }
      setInternalLoading(false)
    }
  }

  const handleNo = () => {
    if (onSkip) {
      onSkip()
    } else if (onCancel) {
      onCancel()
    }
  }

  return (
    <div className={css(styles.overlay)}>
      <div className={css(styles.modal)}>
        <div className={css(styles.header)}>
          <h3 className={css(styles.title)}>Save to Directory?</h3>
        </div>
        <div className={css(styles.content)}>
          <p className={css(styles.message)}>
            Do you want to save this information into your Directory?
          </p>
          {showPreview && (name || email || phoneNumber) && (
            <div className={css(styles.contactPreview)}>
              {name && <div className={css(styles.previewName)}>{name}</div>}
              {email && (
                <div className={css(styles.previewDetail)}>{email}</div>
              )}
              {phoneNumber && (
                <div className={css(styles.previewDetail)}>{phoneNumber}</div>
              )}
            </div>
          )}
          {error && <div className={css(styles.errorMessage)}>{error}</div>}
        </div>
        <div className={css(styles.footer)}>
          <button
            onClick={handleConfirm}
            className={css(styles.confirmBtn)}
            disabled={isLoading}
            type="button"
          >
            {isLoading ? 'Saving...' : 'Yes'}
          </button>
          <button
            onClick={handleNo}
            className={css(styles.cancelBtn)}
            type="button"
            disabled={isLoading}
          >
            No
          </button>
        </div>
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '100%',
    maxWidth: '400px',
    margin: '20px',
  },
  header: {
    padding: '24px 24px 0 24px',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: '#111827',
  },
  content: {
    padding: '16px 24px',
  },
  message: {
    margin: 0,
    fontSize: '14px',
    color: '#374151',
    lineHeight: 1.6,
  },
  contactPreview: {
    marginTop: '16px',
    padding: '12px',
    backgroundColor: '#F9FAFB',
    borderRadius: '6px',
    border: '1px solid #E5E7EB',
  },
  previewName: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#111827',
    marginBottom: '4px',
  },
  previewDetail: {
    fontSize: '13px',
    color: '#6B7280',
    marginTop: '2px',
  },
  errorMessage: {
    marginTop: '12px',
    padding: '8px 12px',
    backgroundColor: '#FEF2F2',
    borderRadius: '6px',
    color: '#DC2626',
    fontSize: '13px',
  },
  footer: {
    padding: '16px 24px 24px 24px',
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
  },
  cancelBtn: {
    padding: '10px 24px',
    backgroundColor: '#F3F4F6',
    color: '#374151',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: '#E5E7EB',
    },
    ':disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  },
  confirmBtn: {
    padding: '10px 24px',
    backgroundColor: '#1C64F2',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: '#1955CC',
    },
    ':disabled': {
      backgroundColor: '#9CA3AF',
      cursor: 'not-allowed',
    },
  },
})

export default SaveToDirectoryDialog
